// Band Stage extras: who's in the band, the records, and the photo wall (admins add, caption, remove, pick the lead photo).
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Disc3, ImagePlus, Star, Trash2, Users } from "lucide-react";
import { API } from "../../App";
import { abs, auth, errMsg } from "../voltbox/vbShared";
import { PhotoGrid, PhotoLightbox } from "../voltbox/PhotoGrid";

const H2 = ({ icon: Icon, eyebrow, children }) => (
  <div className="flex items-center gap-3 mb-4">
    <Icon className="h-6 w-6 text-[#9B59B6]" />
    <div>
      <span className="block text-[10px] font-bold uppercase tracking-[0.25em] text-[#9B59B6]">{eyebrow}</span>
      <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A] leading-tight m-0">{children}</h2>
    </div>
  </div>
);

export const BandMembers = ({ members = [] }) => !members.length ? null : (
  <section className="mb-10" data-testid="band-members">
    <H2 icon={Users} eyebrow="The band">Who you&rsquo;ll hear</H2>
    <div className="flex flex-wrap gap-3">
      {members.map((m) => (
        <div key={m.name} className="rounded-2xl bg-white border border-[#E2E0DC] px-4 py-3" data-testid="band-member">
          <p className="m-0 font-bold text-[#1A1A1A]">{m.name}{m.nick ? <span className="text-[#5C5C5C] font-normal"> &ldquo;{m.nick}&rdquo;</span> : null}</p>
          {m.role && <p className="m-0 text-xs text-[#5C5C5C] mt-0.5">{m.role}</p>}
        </div>
      ))}
    </div>
  </section>
);

export const BandDiscography = ({ records = [] }) => !records.length ? null : (
  <section className="mb-10" data-testid="band-discography">
    <H2 icon={Disc3} eyebrow="Discography">The records</H2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {records.map((r, i) => (
        <a key={`${r.title}-${i}`} href={r.url || undefined} target="_blank" rel="noopener noreferrer" className="group flex gap-3 rounded-2xl bg-white border border-[#E2E0DC] p-3 no-underline hover:-translate-y-0.5 transition-transform" data-testid={`band-record-${i}`}>
          <div className="h-16 w-16 shrink-0 rounded-xl bg-[#1A1A2E] overflow-hidden flex items-center justify-center">
            {r.cover_url ? <img src={abs(r.cover_url)} alt="" className="h-full w-full object-cover" /> : <Disc3 className="h-7 w-7 text-white/40" />}
          </div>
          <div className="min-w-0">
            <p className="m-0 font-bold text-[#1A1A1A] group-hover:underline leading-snug">{r.title}</p>
            <p className="m-0 text-[11px] text-[#5C5C5C] mt-0.5">{[r.year, r.format, r.label].filter(Boolean).join(" · ")}</p>
            {r.note && <p className="m-0 text-[11px] text-[#5C5C5C] mt-1 line-clamp-2">{r.note}</p>}
          </div>
        </a>
      ))}
    </div>
  </section>
);

const AdminPhotoBar = ({ slug, photo, onChanged }) => {
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e, "Couldn't do that")); } };
  return (
    <div className="absolute bottom-1.5 right-1.5 flex gap-1">
      <button type="button" onClick={() => act(() => axios.put(`${API}/admin/bands/${slug}/hero`, { photo_id: photo.photo_id }, auth()), "Lead photo set")} className="h-7 w-7 rounded-full bg-black/70 text-[#FFB347] flex items-center justify-center" title="Use as the lead photo" data-testid={`band-photo-hero-${photo.photo_id}`}><Star className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => window.confirm("Remove this photo?") && act(() => axios.delete(`${API}/admin/bands/${slug}/photos/${photo.photo_id}`, auth()), "Removed")} className="h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center" title="Remove" data-testid={`band-photo-remove-${photo.photo_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
};

export const BandPhotoWall = ({ slug, photos = [], isAdmin, onChanged }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const upload = async (files) => {
    setBusy(true);
    for (const f of [...(files || [])]) {
      const fd = new FormData(); fd.append("file", f);
      try { await axios.post(`${API}/admin/bands/${slug}/photos`, fd, auth()); } catch (e) { toast.error(`${f.name}: ${errMsg(e, "couldn't upload")}`); }
    }
    setBusy(false); onChanged();
  };
  if (!photos.length && !isAdmin) return null;
  return (
    <section className="mb-10" data-testid="band-photo-wall">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H2 icon={ImagePlus} eyebrow="Photo wall">On stage &amp; off</H2>
        {isAdmin && <button type="button" onClick={() => input.current?.click()} disabled={busy} className="mb-4 rounded-full bg-[#1A1A1A] text-white px-4 py-2 text-xs font-bold disabled:opacity-50" data-testid="band-photo-add">{busy ? "Uploading…" : "+ Add photos (admin)"}</button>}
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="band-photo-input" />
      </div>
      {photos.length ? <PhotoGrid photos={photos} columns={3} onOpen={setZoom} tileClass="bg-black/5" testid="band-photo-grid" renderBadge={isAdmin ? (p) => <AdminPhotoBar slug={slug} photo={p} onChanged={onChanged} /> : undefined} />
        : <p className="text-sm text-[#5C5C5C]">No photos yet — add some above. They show whole, never cropped.</p>}
      {zoom != null && <PhotoLightbox photos={photos} index={zoom} onClose={() => setZoom(null)} />}
    </section>
  );
};
